"use client";

import {
  MessageSquare, Search, ShieldCheck, Truck,
  Guitar, MapPin, ChevronRight, Sparkles, ArrowRight, Phone,
} from "lucide-react";

const steps = [
  {
    icon: MessageSquare,
    step: "01",
    title: "Tiếp Nhận Yêu Cầu",
    description:
      "Khách hàng chia sẻ dòng đàn mong muốn, ngân sách và các tiêu chí đặc biệt. Chúng tôi tư vấn và xác nhận yêu cầu chi tiết.",
  },
  {
    icon: Search,
    step: "02",
    title: "Tìm Kiếm Guitar Cũ",
    description:
      "Đội ngũ chuyên gia nhiều năm kinh nghiệm tìm đúng model, đúng ngân sách từ các nguồn uy tín tại TPHCM.",
  },
  {
    icon: ShieldCheck,
    step: "03",
    title: "Kiểm Tra Chi Tiết",
    description:
      "Mỗi cây đàn được kiểm tra kỹ: âm thanh, phím đàn, cần đàn, tuning stability – đảm bảo tình trạng thực tế trước khi nhận.",
  },
  {
    icon: Truck,
    step: "04",
    title: "Giao Tận Nơi",
    description:
      "Giao hàng tận nơi tại TPHCM. Hỗ trợ kiểm tra lại khi nhận và bảo hành dịch vụ sau mua.",
  },
];

const tags = [
  "Mua guitar ở TPHCM",
  "Guitar ở Sài Gòn",
  "Guitar Quận 12",
  "Guitar đã qua sử dụng",
];

export default function BuyGuitarOnRequestSection() {
  return (
    <section
      aria-labelledby="buy-guitar-heading"
      className="relative overflow-hidden py-14 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: "var(--background)" }}
    >
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div
          className="absolute -top-24 -left-24 h-64 w-64 sm:h-96 sm:w-96 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(200,169,110,0.08)" }}
        />
        <div
          className="absolute -bottom-24 -right-24 h-64 w-64 sm:h-96 sm:w-96 rounded-full blur-3xl"
          style={{ backgroundColor: "rgba(200,169,110,0.05)" }}
        />
      </div>

      <div className="relative mx-auto max-w-6xl">

        {/* ══════════════════════════════════════
            HEADER
        ══════════════════════════════════════ */}
        <div className="mb-10 sm:mb-14 lg:mb-16 text-center">

          {/* Location badge */}
          <div
            className="mb-4 sm:mb-6 inline-flex items-center gap-1.5 sm:gap-2 rounded-full border px-3.5 sm:px-4 py-1.5"
            style={{
              borderColor: "rgba(200,169,110,0.35)",
              backgroundColor: "rgba(200,169,110,0.08)",
            }}
          >
            <MapPin className="h-3 w-3 sm:h-3.5 sm:w-3.5" style={{ color: "var(--brand-primary)" }} />
            <span
              className="text-[10px] sm:text-xs font-bold uppercase tracking-widest"
              style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
            >
              Quận 12 · TPHCM · Sài Gòn
            </span>
          </div>

          {/*
            Headline:
            mobile  → text-2xl (24px) fits without overflow
            sm      → text-3xl
            md      → text-4xl / text-5xl
            lg      → text-6xl
          */}
<h2
  id="buy-guitar-heading"
  className="mb-4 sm:mb-6 px-1"
  style={{ fontFamily: "var(--font-montserrat)", wordBreak: "keep-all" }}
>
  {/* Line 1: light weight label — tạo contrast với dòng chính */}
  <span
    className="block text-sm sm:text-base font-medium tracking-[0.18em] uppercase mb-1.5 sm:mb-2"
    style={{ color: "var(--muted-foreground)" }}
  >
    Dịch Vụ
  </span>

  {/* Line 2: hero text — gold, thick, điểm nhấn chính */}
  <span
    className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight"
    style={{ color: "var(--brand-primary)" }}
  >
    Mua Guitar Cũ
  </span>

  {/* Line 3: location — regular weight, muted, nhỏ hơn để không cạnh tranh */}
  <span
    className="block text-base sm:text-lg md:text-xl font-normal mt-1.5 sm:mt-2 tracking-wide"
    style={{ color: "var(--muted-foreground)" }}
  >
    tại{" "}
    <span
      className="font-semibold"
      style={{ color: "var(--foreground)" }}
    >
      Quận 12 – TPHCM
    </span>
  </span>
</h2>

          {/* Gold divider */}
          <div className="mx-auto mb-5 sm:mb-8 flex items-center justify-center gap-2 sm:gap-3">
            <div
              className="h-px w-10 sm:w-16 rounded-full"
              style={{ background: "linear-gradient(to right, transparent, rgba(200,169,110,0.5))" }}
            />
            <Guitar className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "var(--brand-primary)" }} />
            <div
              className="h-px w-10 sm:w-16 rounded-full"
              style={{ background: "linear-gradient(to left, transparent, rgba(200,169,110,0.5))" }}
            />
          </div>

          {/* Body copy — comfortable read width on all sizes */}
          <p
            className="mx-auto max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-3xl text-sm sm:text-base lg:text-lg leading-relaxed px-1"
            style={{ color: "var(--muted-foreground)", fontFamily: "var(--font-roboto)" }}
          >
            Nếu bạn chưa biết{" "}
            <strong style={{ color: "var(--foreground)" }}>mua guitar ở TPHCM</strong> ở đâu uy tín,
            hoặc muốn tìm một cây{" "}
            <strong style={{ color: "var(--foreground)" }}>guitar đã qua sử dụng</strong>{" "}
            đúng nhu cầu nhưng không có kinh nghiệm kiểm tra,{" "}
            <span className="font-semibold" style={{ color: "var(--brand-primary)" }}>Guitar Sài Gòn</span>{" "}
            cung cấp dịch vụ{" "}
            <strong style={{ color: "var(--foreground)" }}>mua guitar theo yêu cầu tại Quận 12</strong>.
          </p>
        </div>

        {/* ══════════════════════════════════════
            STEPS
        ══════════════════════════════════════ */}
        <div className="mb-10 sm:mb-14 lg:mb-16">

          {/* Section label row */}
          <div className="mb-6 sm:mb-10 flex items-center gap-3 sm:gap-4">
            <h3
              className="text-lg sm:text-2xl md:text-3xl font-black shrink-0"
              style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
            >
              Quy Trình Mua Hộ Guitar
            </h3>
            <div className="flex-1 h-px" style={{ backgroundColor: "var(--border)" }} />
            <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" style={{ color: "var(--brand-primary)" }} />
          </div>

          {/*
            Grid:
            mobile  → 1 col (full width, comfortable read)
            sm      → 2 col
            lg      → 4 col
          */}
          <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl border transition-all duration-300"
                  style={{
                    backgroundColor: "var(--card)",
                    borderColor: "var(--border)",
                  }}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = "rgba(200,169,110,0.4)";
                    el.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = "var(--border)";
                    el.style.boxShadow = "none";
                  }}
                >
                  {/* Hover gradient overlay */}
                  <div
                    className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: "linear-gradient(135deg, rgba(200,169,110,0.05) 0%, transparent 60%)" }}
                  />

                  {/* Big step number watermark */}
                  <span
                    className="absolute right-3 top-2 font-black leading-none select-none text-5xl sm:text-6xl transition-colors duration-300"
                    style={{
                      color: "rgba(200,169,110,0.08)",
                      fontFamily: "var(--font-montserrat)",
                    }}
                  >
                    {item.step}
                  </span>

                  {/*
                    Mobile: horizontal layout (icon left, text right) for compact cards
                    sm+:    vertical layout
                  */}
                  <div className="relative p-4 sm:p-5 lg:p-6 flex sm:flex-col gap-4 sm:gap-0">

                    {/* Icon box */}
                    <div
                      className="shrink-0 inline-flex h-10 w-10 sm:h-11 sm:w-11 sm:mb-4 items-center justify-center rounded-xl border"
                      style={{
                        backgroundColor: "rgba(200,169,110,0.1)",
                        borderColor: "rgba(200,169,110,0.25)",
                      }}
                    >
                      <Icon className="h-4 w-4 sm:h-5 sm:w-5" style={{ color: "var(--brand-primary)" }} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="mb-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest sm:mb-1"
                        style={{ color: "var(--muted-foreground)", fontFamily: "var(--font-montserrat)" }}
                      >
                        Bước {item.step}
                      </p>

                      <h4
                        className="mb-1.5 sm:mb-3 text-sm sm:text-base font-black leading-snug"
                        style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
                      >
                        {item.title}
                      </h4>

                      <p
                        className="text-xs sm:text-sm leading-relaxed"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        {item.description}
                      </p>
                    </div>

                  </div>

                  {/* Bottom gold slide-in bar on hover */}
                  <div
                    className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full transition-all duration-500"
                    style={{ background: "linear-gradient(to right, var(--brand-primary), transparent)" }}
                  />
                </div>
              );
            })}
          </div>

          {/* Arrow connectors between steps — desktop only */}
          <div className="mt-3 hidden lg:flex items-center justify-around px-4">
            {steps.slice(0, -1).map((_, i) => (
              <ChevronRight
                key={i}
                className="h-4 w-4"
                style={{ color: "rgba(200,169,110,0.3)" }}
              />
            ))}
          </div>
        </div>

        {/* ══════════════════════════════════════
            SEO TAGS
        ══════════════════════════════════════ */}
        <div className="mb-8 sm:mb-12 flex flex-wrap justify-center gap-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1.5 rounded-full border text-xs font-medium cursor-default transition-all duration-200"
              style={{
                borderColor: "var(--border)",
                backgroundColor: "var(--card)",
                color: "var(--muted-foreground)",
              }}
              onMouseEnter={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = "rgba(200,169,110,0.4)";
                el.style.color = "var(--brand-primary)";
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget as HTMLElement;
                el.style.borderColor = "var(--border)";
                el.style.color = "var(--muted-foreground)";
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* ══════════════════════════════════════
            CTA — full redesign, mobile-first
        ══════════════════════════════════════ */}
        <div
          className="rounded-2xl sm:rounded-3xl border overflow-hidden relative"
          style={{
            backgroundColor: "var(--card)",
            borderColor: "rgba(200,169,110,0.3)",
            boxShadow: "0 0 48px rgba(200,169,110,0.06)",
          }}
        >
          {/* Radial glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: "radial-gradient(ellipse at center, rgba(200,169,110,0.06) 0%, transparent 70%)",
            }}
          />
          {/* Gold top bar */}
          <div
            className="h-1"
            style={{ background: "linear-gradient(to right, var(--brand-primary), rgba(200,169,110,0.3))" }}
          />

          <div className="relative p-6 sm:p-8 text-center">
            <p
              className="mb-1 text-[10px] sm:text-xs font-bold uppercase tracking-widest"
              style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
            >
              Sẵn sàng tư vấn miễn phí
            </p>
            <h3
              className="text-base sm:text-xl font-black mb-1.5 sm:mb-2"
              style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
            >
              Tìm đúng cây đàn bạn cần
            </h3>
            <p
              className="text-xs sm:text-sm mb-6 sm:mb-7 max-w-xs sm:max-w-md mx-auto"
              style={{ color: "var(--muted-foreground)" }}
            >
              Liên hệ ngay để được hỗ trợ tìm guitar phù hợp tại{" "}
              <strong style={{ color: "var(--foreground)" }}>TPHCM</strong> — không mất phí tư vấn.
            </p>

            {/* Buttons — stacked on mobile, side by side on sm+ */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="tel:0325805893"
                className="group inline-flex items-center justify-center gap-2 font-black rounded-xl transition-all duration-200 hover:opacity-90 active:scale-[0.98] w-full sm:w-auto px-6"
                style={{
                  backgroundColor: "var(--brand-primary)",
                  color: "var(--brand-primary-foreground)",
                  fontFamily: "var(--font-montserrat)",
                  fontSize: "0.875rem",
                  height: "52px",
                  boxShadow: "0 4px 16px rgba(200,169,110,0.3)",
                }}
              >
                <Phone className="w-4 h-4" />
                Gọi ngay: 0325 805 893
              </a>
              <a
                href="/danh-muc/guitar-cu-second-hand"
                className="group inline-flex items-center justify-center gap-2 font-bold rounded-xl border transition-all duration-150 active:scale-[0.98] w-full sm:w-auto px-6"
                style={{
                  borderColor: "rgba(200,169,110,0.35)",
                  color: "var(--brand-primary)",
                  backgroundColor: "rgba(200,169,110,0.06)",
                  fontFamily: "var(--font-montserrat)",
                  fontSize: "0.875rem",
                  height: "52px",
                }}
              >
                Xem đàn cũ hiện có
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}